import { useEffect, useLayoutEffect, useState, type RefObject } from 'react';

/** How long the pointer rests on an item before its name shows. */
export const TOOLTIP_DELAY_MS = 300;
/** Time to cross from the item onto its tooltip, which keeps it open (WCAG 1.4.13). */
export const TOOLTIP_GRACE_MS = 100;

export interface TooltipTimer<T> {
  pointerEnter(target: T): void;
  /** The pointer left the target or the tooltip. */
  pointerLeave(): void;
  /** The pointer reached the tooltip. */
  tooltipEnter(): void;
  /** `keyboard`: the focus is :focus-visible. A click's focus leaves the tooltip to the hover. */
  focus(target: T, keyboard: boolean): void;
  blur(target: T): void;
  escape(): void;
  hide(): void;
  dispose(): void;
}

/**
 * When the tooltip shows and for which target: after a rest on hover, at once on keyboard focus, and at once when
 * the pointer moves on to another target while one shows. `onChange` gets the target, or null to hide.
 */
export function createTooltipTimer<T>(onChange: (target: T | null) => void): TooltipTimer<T> {
  let shown: T | null = null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const cancel = () => {
    clearTimeout(timer);
    timer = undefined;
  };
  const show = (target: T | null) => {
    cancel();
    if (target === shown) return;
    shown = target;
    onChange(target);
  };
  const later = (ms: number, target: T | null) => {
    cancel();
    timer = setTimeout(() => show(target), ms);
  };
  return {
    pointerEnter(target) {
      if (target === shown) cancel();
      else if (shown !== null) show(target);
      else later(TOOLTIP_DELAY_MS, target);
    },
    pointerLeave() {
      if (shown === null) cancel();
      else later(TOOLTIP_GRACE_MS, null);
    },
    tooltipEnter: cancel,
    focus(target, keyboard) {
      if (keyboard) show(target);
    },
    blur(target) {
      if (target === shown) show(null);
    },
    escape: () => show(null),
    hide: () => show(null),
    dispose: cancel,
  };
}

/** Drawn for sighted users only: the link or button keeps its own name, so a screen reader reads it once. */
export function TooltipBubble({ text, top, left, onPointerEnter, onPointerLeave }: { text: string; top: number; left: number; onPointerEnter?: () => void; onPointerLeave?: () => void }) {
  return (
    <div className="nav-tooltip" aria-hidden="true" style={{ top, left }} onPointerEnter={onPointerEnter} onPointerLeave={onPointerLeave}>
      {text}
    </div>
  );
}

function isFocusVisible(el: Element): boolean {
  try {
    return el.matches(':focus-visible');
  } catch {
    return true;
  }
}

/**
 * Names the rail's `[data-tooltip]` elements beside them while `enabled(el)` says their label is hidden. Fixed to
 * the viewport, so the rail's scrolling (which clips sideways too) can't cut it off. `refresh` places it again
 * after the rail changes width under it (the collapse toggle).
 */
export function NavTooltip({ rail, enabled, refresh }: { rail: RefObject<HTMLElement | null>; enabled: (el: HTMLElement) => boolean; refresh: unknown }) {
  const [target, setTarget] = useState<HTMLElement | null>(null);
  const [timer] = useState(() => createTooltipTimer<HTMLElement>(setTarget));
  const [place, setPlace] = useState<{ text: string; top: number; left: number } | null>(null);

  useEffect(() => {
    const nav = rail.current;
    if (!nav) return;
    const targetOf = (e: Event) => {
      const el = e.target instanceof Element ? e.target.closest('[data-tooltip]') : null;
      return el instanceof HTMLElement && nav.contains(el) && enabled(el) ? el : null;
    };
    const onOver = (e: PointerEvent) => {
      const el = e.pointerType === 'touch' ? null : targetOf(e);
      if (el) timer.pointerEnter(el);
    };
    const onOut = (e: PointerEvent) => {
      const el = targetOf(e);
      if (el && !(e.relatedTarget instanceof Node && el.contains(e.relatedTarget))) timer.pointerLeave();
    };
    const onFocusIn = (e: FocusEvent) => {
      const el = targetOf(e);
      if (el) timer.focus(el, isFocusVisible(el));
    };
    const onFocusOut = (e: FocusEvent) => {
      if (e.target instanceof HTMLElement) timer.blur(e.target);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && timer.escape();
    nav.addEventListener('pointerover', onOver);
    nav.addEventListener('pointerout', onOut);
    nav.addEventListener('focusin', onFocusIn);
    nav.addEventListener('focusout', onFocusOut);
    nav.addEventListener('scroll', timer.hide);
    window.addEventListener('resize', timer.hide);
    document.addEventListener('keydown', onKey);
    return () => {
      nav.removeEventListener('pointerover', onOver);
      nav.removeEventListener('pointerout', onOut);
      nav.removeEventListener('focusin', onFocusIn);
      nav.removeEventListener('focusout', onFocusOut);
      nav.removeEventListener('scroll', timer.hide);
      window.removeEventListener('resize', timer.hide);
      document.removeEventListener('keydown', onKey);
      timer.dispose();
    };
  }, [rail, enabled, timer]);

  // After the commit, so the toggle's new name and the rail's new width are both in place.
  useLayoutEffect(() => {
    if (!target) return setPlace(null);
    if (!target.isConnected || !enabled(target)) return timer.hide();
    const r = target.getBoundingClientRect();
    setPlace({ text: target.dataset.tooltip ?? '', top: r.top + r.height / 2, left: r.right + 8 });
  }, [target, enabled, timer, refresh]);

  if (!target || !place) return null;
  return <TooltipBubble {...place} onPointerEnter={timer.tooltipEnter} onPointerLeave={timer.pointerLeave} />;
}
